import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Label, LanguageService } from '@services/public';
import {
	HTTP_CACHE_MAX_AGE,
	HTTP_REQUEST_KEY,
	httpExpireRequests,
} from '@util';
import { config } from 'environment';
import { Observable } from 'rxjs';

// Cache expiration in milliseconds.
const CACHE_EXPIRATION = 300000;

export interface User {
	uuid: string;
	name: string;
	email?: string;
	organizationUUID?: string;
}

export const ALL_USERS: User = {
	uuid: null,
	name: '',
};

@Injectable({
	providedIn: 'root',
})
export class LeaderUserService {
	private readonly _key: symbol = Symbol();

	// Page langauge.
	labels: { [key in Label]?: string } = {
		[Label.ALL_USERS]: '',
	};

	constructor(
		private _http: HttpClient,
		private _languageSvc: LanguageService,
	) {
		// Get page language.
		this._languageSvc.getLabels(this.labels);
		ALL_USERS.name = this.labels.allUsers;
	}

	clearCache(): void {
		httpExpireRequests(this._key);
	}

	/**
	 * Retrieves a list of all the users the user has access to. Cached.
	 * @returns {Array<User>} A list of users.
	 */
	getUsers(): Observable<Array<User>> {
		return this._http.get<Array<User>>(`${config.apiBase}leader/user/list`, {
			context: new HttpContext()
				.set(HTTP_REQUEST_KEY, this._key)
				.set(HTTP_CACHE_MAX_AGE, CACHE_EXPIRATION),
		});
	}

	createUser(reqBody) {
		return this._http.post<any>(
			`${config.apiBase}leader/management/user/add`,
			reqBody,
		);
	}

	bulkUploadUsers(reqBody) {
		return this._http.post<any>(
			`${config.apiBase}leader/management/user/bulk`,
			reqBody,
		);
	}

	acceptUserGroupRequest(requredObj) {
		return this._http.post<any>(
			`${config.apiBase}leader/management/group/request/accept`,
			requredObj,
		);
	}

	declineUserGroupRequest(userUUID) {
		return this._http.delete<any>(
			`${config.apiBase}leader/management/group/request/decline`,
			{
				body: { uuid: userUUID },
			},
		);
	}

	deleteUserGroupRequest(userUUID) {
		return this._http.delete<any>(
			`${config.apiBase}leader/management/group/request/delete`,
			{
				body: { uuid: userUUID },
			},
		);
	}

	deleteUserRequest(userUUID) {
		return this._http.delete<any>(
			`${config.apiBase}leader/management/user/request/delete`,
			{
				body: { uuid: userUUID },
			},
		);
	}

	getUserList(query?: any): Observable<any> {
		if (query === undefined) {
			query = '';
		}
		return this._http.get<any>(
			`${config.apiBase}leader/management/user/list${query}`,
		);
	}

	deleteUser(useruuid) {
		return this._http.put<any>(
			`${config.apiBase}leader/management/user/edit`,
			useruuid,
		);
	}

	updateUser(reqBody) {
		return this._http.put<any>(
			`${config.apiBase}leader/management/user/edit`,
			reqBody,
		);
	}

	getUserById(query?: any): Observable<any> {
		return this._http.get<any>(
			`${config.apiBase}leader/management/user/view?uuid=${query}`,
		);
	}

	unsubscribe(reqBody): Observable<any> {
		return this._http.put<any>(
			`${config.apiBase}leader/management/user/edit`,
			reqBody,
		);
	}
}
